import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { Section, ColorField, ToggleField, Field } from "../../shared/components/EditorFields"
import { ImageField } from "../../shared/components/ImageField"
import {
  type PageContentType, PAGECONTENT_FIELD_GROUPS, PAGECONTENT_TYPE_LABELS,
  SERVICES_COMPONENT_FLAGS, SERVICES_DETAIL_FLAGS, SERVICES_FLAG_LABELS,
} from "../types"

type FieldWidget = "text" | "textarea" | "richtext" | "color" | "boolean" | "number" | "url" | "image" | "flags"
const FIELD_META: Partial<Record<string, { widget: FieldWidget; translatable?: boolean; label: string }>> = {
  aboutTitle: { widget: "text", translatable: true, label: "Title" },
  aboutSubtitle: { widget: "richtext", translatable: true, label: "Body" },
  aboutImage: { widget: "image", translatable: true, label: "Image" },
  statementTitle: { widget: "text", translatable: true, label: "Statement" },
  missionImage: { widget: "image", translatable: true, label: "Image" },
  channelsTitle: { widget: "text", translatable: true, label: "Title" },
  channelsSubtitle: { widget: "text", translatable: true, label: "Subtitle" },
  fb: { widget: "text", translatable: true, label: "Facebook label" },
  fbLink: { widget: "url", label: "Facebook link" },
  fbmess: { widget: "text", translatable: true, label: "Messenger label" },
  fbmessLink: { widget: "url", label: "Messenger link" },
  instagram: { widget: "text", translatable: true, label: "Instagram label" },
  instagramLink: { widget: "url", label: "Instagram link" },
  telegram: { widget: "text", translatable: true, label: "Telegram label" },
  telegramLink: { widget: "url", label: "Telegram link" },
  whatsapp: { widget: "text", translatable: true, label: "WhatsApp label" },
  whatsappLink: { widget: "url", label: "WhatsApp link" },
  whatsappc: { widget: "text", translatable: true, label: "WhatsApp (2nd) label" },
  whatsappcLink: { widget: "url", label: "WhatsApp (2nd) link" },
  email: { widget: "text", translatable: true, label: "Email label" },
  emailLink: { widget: "url", label: "Email address" },
  facetime: { widget: "text", translatable: true, label: "FaceTime label" },
  facetimeLink: { widget: "url", label: "FaceTime link" },
  toktok: { widget: "text", translatable: true, label: "TikTok label" },
  tiktokLink: { widget: "url", label: "TikTok link" },
  x: { widget: "text", translatable: true, label: "X label" },
  xLink: { widget: "url", label: "X link" },
  youtube: { widget: "text", translatable: true, label: "YouTube label" },
  youtubeLink: { widget: "url", label: "YouTube link" },
  infoTitle: { widget: "text", translatable: true, label: "Title" },
  infoSubtitle: { widget: "text", translatable: true, label: "Subtitle" },
  servicesTitle: { widget: "text", translatable: true, label: "Title" },
  servicesSubtitle: { widget: "text", translatable: true, label: "Subtitle" },
  // Real checkboxes now that the flag set is known from the Directus form.
  components: { widget: "flags", label: "Components" },
  componentsDetails: { widget: "flags", label: "Service details" },
  textAccessibility: { widget: "text", translatable: true, label: "Accessibility text" },
  textPopulations: { widget: "text", translatable: true, label: "Populations text" },
  textProviders: { widget: "text", translatable: true, label: "Providers text" },
  textTypeOfService: { widget: "text", translatable: true, label: "Type-of-service text" },
  richtextTitle: { widget: "text", translatable: true, label: "Title" },
  richtext: { widget: "richtext", translatable: true, label: "Body (HTML)" },
  categoriesTitle: { widget: "text", translatable: true, label: "Title" },
  categoriesSubtitle: { widget: "text", translatable: true, label: "Subtitle" },
  sectionsTitle: { widget: "text", translatable: true, label: "Title" },
  sectionsSubtitle: { widget: "text", translatable: true, label: "Subtitle" },
  contactName: { widget: "text", translatable: true, label: "Name field label" },
  contactEmail: { widget: "text", translatable: true, label: "Email field label" },
  contactSubject: { widget: "text", translatable: true, label: "Subject field label" },
  contactMessage: { widget: "text", translatable: true, label: "Message field label" },
  popupTitle: { widget: "text", translatable: true, label: "Title" },
  popupText: { widget: "textarea", translatable: true, label: "Text" },
  popupSubtitle: { widget: "text", translatable: true, label: "Subtitle" },
  buttonText: { widget: "text", translatable: true, label: "Button label" },
  buttonLink: { widget: "url", translatable: true, label: "Button URL" },
  text: { widget: "textarea", translatable: true, label: "Text" },
  textBold: { widget: "boolean", label: "Bold" },
  textSize: { widget: "number", label: "Text size" },
  textColor: { widget: "color", label: "Text color" },
  fontWeight: { widget: "number", label: "Font weight" },
  bgColor: { widget: "color", label: "Background color" },
  colorBg: { widget: "color", label: "Background color" },
  colorText: { widget: "color", label: "Text color" },
  image: { widget: "image", translatable: true, label: "Image" },
  latitude: { widget: "number", label: "Latitude" },
  longitude: { widget: "number", label: "Longitude" },
}

export function PageContentFields({
  entity, editLocale, defaultLocale, set, setT,
}: { entity: any; editLocale: string; defaultLocale: string; set: (p: any) => void; setT: (p: any) => void }) {
  const type = entity.type as PageContentType
  const canonicalFields = PAGECONTENT_FIELD_GROUPS[type] ?? []
  const t = entity.translations?.[editLocale] ?? {}

  // Any field the row has a real value for, outside its type's canonical set —
  // still shown, but separated, since it is legacy data (see types.ts's
  // fidelity note 2) rather than something this type is meant to use.
  const foreignPopulated = Object.keys(FIELD_META).filter(f =>
    !canonicalFields.includes(f as any) &&
    entity[f] !== undefined && entity[f] !== null && entity[f] !== "" &&
    !(Array.isArray(entity[f]) && entity[f].length === 0)
  )

  const renderField = (field: string) => {
    const meta = FIELD_META[field]
    if (!meta) return null
    const meta_ = meta
    // A field is edited as a translation only when BOTH it is translatable and
    // we are on a non-source locale. On the source locale the text belongs in
    // the base column — writing it to a translation row instead would leave
    // the source empty and every other locale falling back to nothing.
    const isTranslated = !!meta_.translatable && editLocale !== defaultLocale

    // The value must NOT fall back to the source text. Showing the source as
    // the input's value means typing one character forks the whole source
    // string into this locale as if it had been translated. The fallback is
    // shown as a placeholder instead: visible, but not yours until you type.
    const value = isTranslated ? (t[field] ?? "") : (entity[field] ?? "")
    const fallbackText = isTranslated ? (entity[field] ?? "") : ""
    const placeholder = typeof fallbackText === "string" ? fallbackText : ""

    const onChange = (v: any) => (isTranslated ? setT({ [field]: v }) : set({ [field]: v }))

    if (meta.widget === "boolean") {
      return <ToggleField key={field} label={meta.label} checked={!!entity[field]} onChange={v => set({ [field]: v })} />
    }
    if (meta.widget === "color") {
      return <ColorField key={field} label={meta.label} value={entity[field]} onChange={v => set({ [field]: v })} />
    }
    if (meta.widget === "flags") {
      // Every flag but showIA is a HIDE flag, so unchecked == visible. Rendering
      // them as raw JSON (as this did before) made that polarity invisible and
      // invited the agent to write arbitrary keys.
      const flags = field === "components" ? SERVICES_COMPONENT_FLAGS : SERVICES_DETAIL_FLAGS
      const current: Record<string, any> = entity[field] ?? {}
      const extras = Object.keys(current).filter(k => !(flags as readonly string[]).includes(k))
      return (
        <Field key={field} label={meta.label}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            {flags.map(f => (
              <label key={f} className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={!!current[f]}
                  onChange={e => set({ [field]: { ...current, [f]: e.target.checked } })}
                />
                {SERVICES_FLAG_LABELS[f]}
              </label>
            ))}
          </div>
          {extras.length > 0 && (
            <p className="text-[11px] text-amber-600">
              Also set, outside this form: {extras.join(", ")}. Left untouched.
            </p>
          )}
        </Field>
      )
    }
    if (meta.widget === "image") {
      // Directus kept one file reference per row, shared by every locale. We're
      // replacing Directus, and localised artwork is a real need (text baked
      // into a banner, region-specific imagery), so images translate like any
      // other field: per-locale override, falling back to the source image.
      return (
        <ImageField
          key={field}
          label={isTranslated ? `${meta.label} — ${editLocale}` : meta.label}
          value={value || undefined}
          fallbackValue={placeholder || undefined}
          onChange={v => onChange(v || undefined)}
        />
      )
    }
    if (meta.widget === "number") {
      return (
        <Field key={field} label={meta.label}>
          <Input
            type="number" value={value ?? ""}
            onChange={e => set({ [field]: e.target.value === "" ? undefined : Number(e.target.value) })}
            className="h-8 text-sm"
          />
        </Field>
      )
    }
    if (meta.widget === "textarea" || meta.widget === "richtext") {
      return (
        <Field key={field} label={meta.label} hint={isTranslated ? `Translatable · ${editLocale}` : undefined}>
          <Textarea
            value={value}
            placeholder={placeholder}
            onChange={e => onChange(e.target.value)}
            rows={meta.widget === "richtext" ? 6 : 3}
            className={cn("text-sm", meta.widget === "richtext" && "font-mono")}
          />
        </Field>
      )
    }
    // text / url
    return (
      <Field key={field} label={meta.label} hint={isTranslated ? `Translatable · ${editLocale}` : undefined}>
        <Input value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className="h-8 text-sm" />
      </Field>
    )
  }

  return (
    <>
      <Section title={`${PAGECONTENT_TYPE_LABELS[type] ?? type} block`} defaultOpen>
        {/*
          Type is editable here because it decides which fields the rest of this
          form shows. Changing it deliberately does NOT clear the old type's
          values — see types.ts fidelity note 2: production rows genuinely carry
          fields outside their own type's group, and wiping them on a type
          change would destroy real content. Anything left behind surfaces in
          the "Other fields with data" section below.
        */}
        <Field
          label="Block type"
          hint="Changing this switches which fields are shown. Values from the old type are kept, not cleared — they appear under 'Other fields with data'."
        >
          <Select value={type} onValueChange={v => set({ type: v })}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(PAGECONTENT_TYPE_LABELS) as PageContentType[]).map(t => (
                <SelectItem key={t} value={t} className="text-sm">
                  {PAGECONTENT_TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {/*
          The block's own admin-facing name, not part of any type's canonical
          field set — it exists to identify the block in a list, which is what
          the browser tree shows.
        */}
        <Field label="Title" hint="Identifies this block in the content list. Not shown on the public page.">
          <Input
            value={entity.title ?? ""}
            onChange={e => set({ title: e.target.value || undefined })}
            className="h-8 text-sm"
          />
        </Field>
        <p className="text-[11px] text-muted-foreground">
          Fields for this block's type. Translatable fields show text for {editLocale}, falling back to the source ({defaultLocale}) when empty.
        </p>
        {/*
          latitude/longitude are canonical for "services" but are rendered by
          the Map center section below, so skip them here rather than showing
          each twice.
        */}
        {canonicalFields
          .filter(f => f !== "latitude" && f !== "longitude")
          .map(f => renderField(String(f)))}
      </Section>

      {/*
        Map center belongs to the "services" block ONLY — that is the one type
        that renders a map, and the only one Directus shows Latitude/Longitude
        on. This previously listed richtext/channels/about, which was a guess
        and put a map-center panel on three blocks that have no map.
        A block of another type that somehow carries coordinates still shows
        them, via the "Other fields with data" section below.
      */}
      {type === "services" && (
        <Section title="Map center" defaultOpen={!!entity.latitude || !!entity.longitude}>
          <div className="grid grid-cols-2 gap-2">
            {renderField("latitude")}
            {renderField("longitude")}
          </div>
        </Section>
      )}

      {foreignPopulated.length > 0 && (
        <Section title={`Other fields with data (${foreignPopulated.length})`}>
          <p className="text-[11px] text-amber-600">
            These fields have values but do not belong to this block's type — likely left over from an earlier edit. They are shown so nothing is silently hidden; clear them if they are stale.
          </p>
          {foreignPopulated.map(f => renderField(f))}
        </Section>
      )}
    </>
  )
}

// ── Primitives ───────────────────────────────────────────────────────────────

